import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getAvailablePages } from 'utils';

import {
  getConfigProp,
  isUserNeedKyc,
  isUserNeedPartialVerification,
  isUserUnderVerification,
  SiteConfigManager,
  useApplicationState,
  useUserMessages,
} from '@atlas/core';
import { availableClientProfileRoutes, staticRoutes } from '@atlas/routing';
import { CLIENT_PROFILE_COMPONENT_TYPES } from '@atlas/routing/build/routes/componentTypes';
import { Button, MenuItem } from '@atlas/ui';

import NotificationStatePopup from 'common/Popup/NotificationStatePopup';
import history from 'components/MainRouter/history';

import ClientProfileHeader from './ClientProfileHeader';
import ClientProfileLogout from './ClientProfileLogout';
import ExpandedNavigationItem from './ExpandedNavigationItem';

const ClientProfileNavigation = ({ clientProfilePath, clientProfileTabs = [] }) => {
  const applicationState = useApplicationState();
  const {
    account: { actionsRequiresVerification, verificationUri },
    authenticated,
  } = applicationState;
  const isSportRadarProject = getConfigProp('isSportRadarProject');
  const needKycNotUnderRegulation =
    !isUserUnderVerification(applicationState?.account) &&
    isUserNeedKyc(applicationState?.account) &&
    !!actionsRequiresVerification;
  const { unreadMessagesCount } = useUserMessages();

  const isRegistrationEnabled = SiteConfigManager.getConfig('config.config.enable_registration');

  const availableTabs = useMemo(() => {
    const availablePages = getAvailablePages(availableClientProfileRoutes, clientProfileTabs);

    if (isSportRadarProject) {
      return availablePages.map(page => {
        if (page.type === CLIENT_PROFILE_COMPONENT_TYPES.BONUSES) {
          return {
            ...page,
            tabs: page.tabs.filter(
              tab => tab.type !== CLIENT_PROFILE_COMPONENT_TYPES.BONUSES_FREEBET,
            ),
          };
        }

        return page;
      });
    }

    return availablePages;
  }, [clientProfileTabs, isSportRadarProject]);

  if (!authenticated) {
    return (
      <NotificationStatePopup
        buttons={
          <>
            {isRegistrationEnabled && (
              <Link className="secondary" to={staticRoutes.signup}>
                <Button label="Sign up" size="big" type="secondary" />
              </Link>
            )}
            <Link className="primary" to={staticRoutes.signin}>
              <Button label="Sign in" size="big" type="primary" />
            </Link>
          </>
        }
        buttonsClassName="with-navbar"
        message="To see your profile please sign up or sign in"
        title="You are not logged into your account"
      />
    );
  }

  return (
    <div className="user-profile-group user-profile-group--mb-0">
      <div className="user-profile-group-item">
        <ClientProfileHeader />
        {availableTabs.map(({ icon, isExpandedOnMobile, link, name, tabs, type }) => {
          if (type === 'verification' && !needKycNotUnderRegulation && !verificationUri) {
            return null;
          }

          const isCustomIcon =
            (type === 'verification' && !!actionsRequiresVerification) ||
            (type === 'verification' && isUserNeedPartialVerification(applicationState?.account));

          if (tabs && isExpandedOnMobile) {
            return (
              <ExpandedNavigationItem key={link} link={link} name={name} tabs={tabs} type={type} />
            );
          }

          return (
            <MenuItem
              className="menu-item-container"
              customIcon={isCustomIcon && 'error-legitimuz'}
              icon={icon}
              key={link}
              onClick={() => history.push(`${clientProfilePath}/${link}`)}
              renderArrow
              text={name}
              withNotification={type === 'messages' && !!unreadMessagesCount}
            />
          );
        })}
      </div>
      <ClientProfileLogout />
    </div>
  );
};

ClientProfileNavigation.propTypes = {
  clientProfilePath: PropTypes.string.isRequired,
  clientProfileTabs: PropTypes.array,
};

export default ClientProfileNavigation;
